<template>
    <div class="gauge-chart" :class="[cardSize, { banded: bandList }]">
        <svg :viewBox="viewBox" preserveAspectRatio="xMidYMid meet">
            <!-- 分級儀表：整條弧預先塗成各級的顏色，讀者不必看數字就知道分幾級 -->
            <template v-if="segments.length">
                <path
                    v-for="segment in segments"
                    :key="segment.level"
                    :d="segment.d"
                    class="band-arc"
                    :class="{ dimmed: isDimmed(segment) }"
                    :style="{ stroke: segment.color }"
                />
                <!-- 每一級的名稱與數值範圍，沿著弧排在外圈 -->
                <g
                    v-for="segment in segments"
                    :key="`label-${segment.level}`"
                    class="band-label"
                    :class="{ dimmed: isDimmed(segment) }"
                >
                    <text
                        :x="segment.labelX"
                        :y="segment.labelY"
                        class="band-name"
                        :style="{ fill: isDimmed(segment) ? segment.inkDim : segment.ink }"
                    >
                        {{ $t(`quality-level-short.${segment.level}`) }}
                    </text>
                    <text :x="segment.labelX" :y="segment.labelY + RANGE_OFFSET" class="band-range">
                        {{ segment.range }}
                    </text>
                </g>
            </template>
            <!-- 無分級表時退回原本的單色弧：底環 + 隨數值增長的值環 -->
            <template v-else>
                <path :d="trackPath" class="track" />
                <path v-if="hasValue" :d="valuePath" class="value-arc" :style="{ stroke: color }" />
            </template>

            <!-- 指針 -->
            <g v-if="hasValue">
                <line
                    :x1="CENTER.x"
                    :y1="CENTER.y"
                    :x2="needle.x"
                    :y2="needle.y"
                    class="needle"
                    :style="{ stroke: color }"
                />
                <circle :cx="CENTER.x" :cy="CENTER.y" r="6" class="needle-hub" />
            </g>

            <!-- 刻度端點。分級模式下每一級都標了範圍，頭尾再標一次是多餘的 -->
            <template v-if="!bandList">
                <text :x="CENTER.x - RADIUS" :y="CENTER.y + 20" class="scale-label">
                    {{ min }}
                </text>
                <text :x="CENTER.x + RADIUS" :y="CENTER.y + 20" class="scale-label">
                    {{ max }}
                </text>
            </template>

            <!-- 數值放在弧線下方，不與指針爭同一塊空間 -->
            <text :x="CENTER.x" :y="valueY" class="value-text" :style="{ fill: color }">
                {{ displayValue }}<tspan class="value-unit">{{ unit ? ` ${unit}` : '' }}</tspan>
            </text>
            <!-- 分級名稱：外圈是縮寫，這裡放完整說法 -->
            <text
                v-if="activeBand"
                :x="CENTER.x"
                :y="levelY"
                class="level-text"
                :style="{ fill: color }"
            >
                {{ $t(`quality-level.${activeBand.level}`) }}
            </text>
        </svg>
    </div>
</template>

<script>
// 半圓指針儀表。只適用於有明確上限的指標（百分比、額定容量），
// 否則滿刻度會隨資料浮動，讀者無從判斷當下數值算高還是低。
//
// 傳入 bands 可改用分級模式（見 gaugeBands.js）：各級在弧上等寬，
// 指針位置由「第幾級 + 該級內的比例」決定，而不是照數值線性配置。
// 分級模式會在外圈標出每一級的名稱與數值範圍。

import { levelColor, IDLE_COLOR, bandIndexOf, BAND_VIEWBOX } from './gaugeBands'

// 分級模式要在弧外圈放標籤，畫布比單色模式寬也高
const PLAIN_CENTER = { x: 100, y: 100 }
const PLAIN_RADIUS = 74
const BAND_CENTER = { x: 130, y: 118 }
const BAND_RADIUS = 58
// 標籤圈半徑：要離弧夠遠才不會壓到色帶，又不能超出畫布。
// 級名 10px、數值範圍 8.5px 時，86 會讓兩側的數值範圍壓到弧，實測 96 最近也離弧 4.9
const LABEL_RADIUS = 96
// 級名基線到數值範圍基線的距離
const RANGE_OFFSET = 10.5
// 相鄰弧段之間留一點縫，否則深淺相近的兩級會連成一條看不出分界
const SEGMENT_GAP_DEG = 1.5

// 極座標轉直角座標；0° 在右、180° 在左，y 軸向下故取負
function polar(cx, cy, r, angleDeg) {
    const rad = (angleDeg * Math.PI) / 180
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) }
}

function arcPath(cx, cy, r, fromDeg, toDeg) {
    const start = polar(cx, cy, r, fromDeg)
    const end = polar(cx, cy, r, toDeg)
    const largeArc = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0
    // sweep=1：在 SVG 座標下由左經上方順時針畫到右
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

export default {
    name: 'GaugeChart',
    props: {
        value: { type: [Number, String], default: null },
        min: { type: Number, default: 0 },
        max: { type: Number, default: 100 },
        unit: { type: String, default: '' },
        cardSize: { type: String, default: 'small' },
        // 分級表（gaugeBands.js 的 QUALITY_BANDS 之一）。給了就切到分級模式。
        bands: { type: Array, default: null },
    },
    computed: {
        bandList() {
            return this.bands && this.bands.length ? this.bands : null
        },
        CENTER() {
            return this.bandList ? BAND_CENTER : PLAIN_CENTER
        },
        RADIUS() {
            return this.bandList ? BAND_RADIUS : PLAIN_RADIUS
        },
        numericValue() {
            const n = Number(this.value)
            return this.value === null || this.value === '' || Number.isNaN(n) ? null : n
        },
        hasValue() {
            return this.numericValue !== null
        },
        activeIndex() {
            return this.bandList ? bandIndexOf(this.numericValue, this.bandList) : -1
        },
        activeBand() {
            return this.activeIndex === -1 ? null : this.bandList[this.activeIndex]
        },
        segments() {
            if (!this.bandList) return []
            const { x: cx, y: cy } = BAND_CENTER
            const step = 180 / this.bandList.length
            return this.bandList.map((band, index) => {
                // 弧由左（180°）往右（0°）遞減
                const from = 180 - index * step - SEGMENT_GAP_DEG / 2
                const to = 180 - (index + 1) * step + SEGMENT_GAP_DEG / 2
                const middle = 180 - (index + 0.5) * step
                const at = polar(cx, cy, LABEL_RADIUS, middle)
                return {
                    index,
                    level: band.level,
                    color: band.color,
                    ink: band.ink,
                    inkDim: band.inkDim || band.ink,
                    range: `${band.from}–${band.to}`,
                    labelX: at.x,
                    labelY: at.y,
                    d: arcPath(cx, cy, BAND_RADIUS, from, to),
                }
            })
        },
        // 指針位置夾在刻度範圍內，但中央數字仍顯示真實值
        ratio() {
            if (!this.hasValue) return 0
            if (this.bandList) {
                const band = this.bandList[this.activeIndex]
                const span = band.to - band.from || 1
                const within = Math.min(1, Math.max(0, (this.numericValue - band.from) / span))
                return (this.activeIndex + within) / this.bandList.length
            }
            const span = this.max - this.min || 1
            return Math.min(1, Math.max(0, (this.numericValue - this.min) / span))
        },
        angle() {
            return 180 - this.ratio * 180
        },
        color() {
            if (!this.hasValue) return IDLE_COLOR
            return this.activeBand ? this.activeBand.ink : levelColor(this.numericValue)
        },
        // 中卡的級名縮小了，名稱與數值範圍的間距跟著等比例收
        RANGE_OFFSET() {
            return this.cardSize === 'medium' ? 8.5 : RANGE_OFFSET
        },
        viewBox() {
            if (!this.bandList) return '0 0 200 160'
            const { x, y, width, height } = BAND_VIEWBOX
            return `${x} ${y} ${width} ${height}`
        },
        // 分級模式的數值與級名縮小（PM 09-14：外圈級名放大、下方數字縮小），位置跟著往上收
        valueY() {
            return this.CENTER.y + (this.bandList ? 28 : 50)
        },
        levelY() {
            return this.CENTER.y + 44
        },
        trackPath() {
            return arcPath(PLAIN_CENTER.x, PLAIN_CENTER.y, PLAIN_RADIUS, 180, 0)
        },
        valuePath() {
            return arcPath(PLAIN_CENTER.x, PLAIN_CENTER.y, PLAIN_RADIUS, 180, this.angle)
        },
        needle() {
            // 指針長度隨半徑縮放，兩種模式看起來才一樣長
            return polar(this.CENTER.x, this.CENTER.y, this.RADIUS * 0.78, this.angle)
        },
        displayValue() {
            return this.hasValue ? this.numericValue : '—'
        },
    },
    methods: {
        // 有讀值、且不是讀值所在的那一級，就淡化
        isDimmed(segment) {
            return this.activeIndex !== -1 && segment.index !== this.activeIndex
        },
    },
}
</script>

<style lang="scss" scoped>
.gauge-chart {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        width: 100%;
        height: 100%;
    }
}

.track {
    fill: none;
    stroke: #e6ebef;
    stroke-width: 16;
    stroke-linecap: round;
}

.value-arc {
    fill: none;
    stroke-width: 16;
    stroke-linecap: round;
    transition: stroke 0.3s;
}

/* 非當前級數淡化，指針落點才會第一眼跳出來 */
.band-arc {
    fill: none;
    stroke-width: 13;
    stroke-linecap: butt;
    transition: opacity 0.3s;

    &.dimmed {
        opacity: 0.45;
    }
}

/* 級名與數值範圍不用透明度，直接換成對照表上的淡色（級名見 gaugeBands.js 的 inkDim） */
.band-label.dimmed .band-range {
    fill: #9a9a9a;
}

.band-name {
    text-anchor: middle;
    font-size: 10px;
    font-weight: bold;
    transition: fill 0.3s;
}

.band-range {
    text-anchor: middle;
    font-size: 8.5px;
    fill: #6b6b6b;
    transition: fill 0.3s;
}

/* 上面的 10px／8.5px 是給放大後看的。中卡的畫布實測被放大約 2 倍，
   同樣的字級在螢幕上是 21px，比卡片標題（16px）還大（PM 09-14）。
   中卡改成 8px／7px（試過 7px／6px 又嫌太小），螢幕上約 17px／14.5px。
   字只會變小，BAND_VIEWBOX 量出來的邊界仍然夠用 */
.medium {
    .band-name {
        font-size: 8px;
    }

    .band-range {
        font-size: 7px;
    }
}

.needle {
    stroke-width: 3;
    stroke-linecap: round;
}

.needle-hub {
    fill: #475569;
}

.scale-label {
    text-anchor: middle;
    font-size: 12px;
    fill: #a0aec0;
}

.value-text {
    text-anchor: middle;
    font-size: 30px;
    font-weight: bold;
    transition: fill 0.3s;
}

.value-unit {
    font-size: 15px;
    font-weight: normal;
}

.banded {
    .value-text {
        font-size: 16px;
    }

    .value-unit {
        font-size: 9px;
    }

    .level-text {
        font-size: 11px;
    }
}

.level-text {
    text-anchor: middle;
    font-size: 15px;
    font-weight: bold;
    transition: fill 0.3s;
}
</style>
